import type { InitSummary } from './types/InitSummary.js';
import type { GeneratedPackageChanges } from './types/GeneratedPackageChanges.js';
import type {
  InitPackageResult,
  InitPackageResultErrors,
  InitPackageResultWarnings,
} from './types/InitPackageResult.js';
import type { InitOptions } from './types/InitOptions.js';
import type { BundleMessage } from '@ms-cloudpack/common-types';
import { requiredChangeTypes, type GeneratedChange } from './types/GeneratedChange.js';
import type { EnsurePackageBundledResult } from '@ms-cloudpack/api-server';

/**
 * Creates a summary tracking helper for the `init` verb to use to record statistics about results and needed
 * changes. The `record*` methods of the returned object will be used within init to add the stats, and the
 * `summarize` method will return the final tally. The resulting `InitSummary` object can be used in the
 * `formatInitSummary` helper to convert it to a string for the user.
 */
export class InitSummaryData {
  /** Set this to indicate that the generated config was deleted. */
  public deletedGeneratedConfig = false;

  /** Mapping from absolute package path to results */
  private _packageResults: Record<string, InitPackageResult>;
  /** Mapping from absolute package path to generated config updates */
  private _generatedUpdates: Record<string, GeneratedPackageChanges>;
  private _startTime: number;
  private _appPath: string;
  private _check: boolean;

  constructor(
    params: Pick<InitOptions, 'check'> & {
      appPath: string;
    },
  ) {
    this._packageResults = {};
    this._generatedUpdates = {};
    this._startTime = Date.now();
    this._appPath = params.appPath;
    this._check = !!params.check;
  }

  /**
   * Returns whether any generated changes have been recorded.
   */
  public hasChanges(): boolean {
    return Object.values(this._generatedUpdates).length > 0;
  }

  /**
   * Saves a package bundle result. Overwrites the previous result if there was one.
   * @param packagePath - Absolute package path
   */
  public recordResult(
    packagePath: string,
    result: Pick<EnsurePackageBundledResult, 'name' | 'version' | 'info' | 'outputPath' | 'errors' | 'warnings'>,
  ): void {
    this._packageResults[packagePath] = {
      path: packagePath,
      name: result.name,
      version: result.version,
      // this is mainly for easier testing: don't set properties if they'll be undefined
      ...(result.info && { info: result.info }),
      ...(result.outputPath && { outputPath: result.outputPath }),
      ...(result.errors && { errors: result.errors }),
      ...(result.warnings && { warnings: result.warnings }),
    };
  }

  /**
   * Record an exception for a given package path.
   * This creates a bundle message like "Error thrown while (context): (error)".
   *
   * (Unlike `addErrors`, this will create an overall package result if one doesn't exist yet.)
   */
  public recordException(params: {
    /** Absolute package path */
    packagePath: string;
    /** Error object or other caught item */
    error: unknown;
    /** Bundler or step that generated this message. */
    source: string;
    /** Description of the context in which the error was thrown, e.g. "evaluating package" */
    context: string;
    /** Package name in case no result has been recorded for this package yet */
    name?: string;
    /** Package version in case no result has been recorded for this package yet */
    version?: string;
  }): void {
    const { packagePath, error, source, context, name = '<unknown>', version = '<unknown>' } = params;
    if (!this._packageResults[packagePath]) {
      this.recordResult(packagePath, { name, version });
    }

    const text = `Error thrown while ${context}: ${(error as Error).stack || error}`;
    this.addErrors(packagePath, [{ text, source }]);
  }

  /**
   * Record more errors for a given package path.
   * @param packagePath - Absolute package path
   */
  public addErrors(packagePath: string, errors: BundleMessage[]): void {
    const packageResult = this._packageResults[packagePath];
    if (!packageResult) {
      throw new Error(`Package result not found for path ${packagePath}`);
    }

    packageResult.errors = [...(packageResult.errors || []), ...errors];
  }

  /**
   * Record more warnings for a given package path.
   * @param packagePath - Absolute package path
   */
  public addWarnings(packagePath: string, warnings: BundleMessage[]): void {
    const packageResult = this._packageResults[packagePath];
    if (!packageResult) {
      throw new Error(`Package result not found for path ${packagePath}`);
    }

    packageResult.warnings = [...(packageResult.warnings || []), ...warnings];
  }

  /**
   * Get the recorded package results.
   * @returns Mapping from absolute package path to results
   */
  public getResults(): Record<string, InitPackageResult> {
    return this._packageResults;
  }

  /**
   * Get the recorded generated config updates.
   * @returns Mapping from absolute package path to generated config updates
   */
  public getGeneratedChanges(): Record<string, GeneratedPackageChanges> {
    return this._generatedUpdates;
  }

  /**
   * Save generated config updates, potentially from multiple packages.
   * @param allChanges - The generated config update to save.
   */
  public recordGeneratedChanges(allChanges: GeneratedPackageChanges[]): void {
    for (const update of allChanges) {
      const { name, version, path, changes } = update;
      // Only record changes if there are any, so that hasChanges() is accurate.
      if (changes.length) {
        const updatesForPackage = (this._generatedUpdates[path] ??= { name, version, path, changes: [] });

        updatesForPackage.changes.push(...changes);
      }
    }
  }

  /**
   * Returns the summary of the results.
   */
  public summarize(): InitSummary {
    const duration = Date.now() - this._startTime;
    let totalSuccess = 0;
    let totalErrors = 0;
    let totalWarnings = 0;
    let totalChanges = 0;
    let totalRequiredChanges = 0;
    const packagesWithErrors: InitPackageResultErrors[] = [];
    const packagesWithWarnings: InitPackageResultWarnings[] = [];

    for (const packageResult of Object.values(this._packageResults)) {
      if (packageResult.errors?.length) {
        packagesWithErrors.push(packageResult as InitPackageResultErrors);
        totalErrors += packageResult.errors.length;
      } else {
        totalSuccess++;
      }

      if (packageResult.warnings?.length) {
        packagesWithWarnings.push(packageResult as InitPackageResultWarnings);
        totalWarnings += packageResult.warnings.length;
      }
    }

    const sortedUpdates = Object.values(this._generatedUpdates).sort((a, b) => a.path.localeCompare(b.path));

    for (const updates of sortedUpdates) {
      totalChanges += updates.changes.length;
      totalRequiredChanges += updates.changes.filter((u) => requiredChangeTypes[u.type]).length;
      sortChanges(updates.changes);
    }

    return {
      appPath: this._appPath,
      duration,
      totalPackages: Object.keys(this._packageResults).length,
      totalSuccess,
      totalErrors,
      totalWarnings,
      totalChanges,
      totalRequiredChanges,
      failed: totalErrors > 0 || (this._check && totalRequiredChanges > 0),
      packagesWithErrors,
      packagesWithWarnings,
      generatedFileUpdates: sortedUpdates,
      deletedGeneratedConfig: this.deletedGeneratedConfig,
    };
  }
}

/**
 * Sort generated changes in-place alphabetically by `.change` (description).
 * Returns the array for convenience (though it's the same object).
 */
export function sortChanges(changes: GeneratedChange[]): GeneratedChange[] {
  return changes.sort((a, b) => a.change.localeCompare(b.change));
}
